// The user's chat inbox over `/ws` (`chat_subscribe`), resuming from the last
// processed chat cursor after a reconnect. The caller applies the frames and
// repairs its cache through REST when the stream could not be resumed.
import type { WsClientMessage, WsServerMessage } from "@10twenty/api";
import { RealtimeStream, type RealtimeStreamDeps } from "../live/circleStream";
import type { ChatFrame } from "./chat";

export interface ChatStreamDeps extends RealtimeStreamDeps {
  /** Cursor of the last processed chat frame, to resume from. */
  cursor: () => string | null;
  onFrame: (frame: ChatFrame) => void;
}

export class ChatStream extends RealtimeStream {
  private readonly chat: ChatStreamDeps;
  private readonly state: { live: boolean };

  constructor(deps: ChatStreamDeps) {
    const state = { live: false };
    super({
      ...deps,
      onStatus: (status) => {
        state.live = status === "live";
        deps.onStatus(status);
      },
    });
    this.chat = deps;
    this.state = state;
  }

  /** Sends `typing` if the inbox subscription is live; returns whether it went out. */
  typing(conversationId: string): boolean {
    if (!this.state.live) return false;
    this.send({ type: "typing", conversation_id: conversationId });
    return true;
  }

  protected subscription(): WsClientMessage {
    const cursor = this.chat.cursor();
    return { type: "chat_subscribe", ...(cursor ? { cursor } : {}) };
  }

  protected handle(frame: WsServerMessage): void {
    switch (frame.type) {
      case "chat_subscribed":
        this.markLive();
        this.chat.onFrame(frame);
        return;
      case "chat_resync":
      case "chat_message":
      case "chat_conversation":
      case "chat_read":
      case "chat_receipt":
      case "chat_typing":
        this.chat.onFrame(frame);
        return;
      default:
      // Circle frames and errors: a failed
      // chat_subscribe leaves the stream connecting, and the inbox works over REST.
    }
  }
}
